import { cloneElement, HtmlHTMLAttributes, memo, ReactNode, useEffect, useId, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import * as PopperJS from "@popperjs/core";
import classNames from "classnames";
import { Modifier, usePopper } from "react-popper";
import { v1 } from "uuid";

import styles from "./style.module.scss";

export type DropdownProps = {
  isOpen?: boolean,
  onRequestClose?: () => void,
  referenceElement?: Element | PopperJS.VirtualElement | null,
  children?: ReactNode | ReactNode [],
  arrow?: ReactNode,
  overlayClassName?: string,
  className?: string,
  id?: string,
  closeOnClick?: "always" | "outOfDropDown" | "outOfContainer" | "overlay" | "never",
  outOfContainer?: Node,
  closeOnWheel?: boolean,
  closeOnEsc?: boolean,
  popperOptions?: Omit<Partial<PopperJS.Options>, "modifiers"> & {
    createPopper?: typeof PopperJS.createPopper;
    modifiers?: ReadonlyArray<Modifier<any>>;
  }
  overlayProps?: HtmlHTMLAttributes<HTMLDivElement>
}

export const Dropdown = memo<DropdownProps>(
  (
    {
      id,
      isOpen,
      onRequestClose,
      referenceElement,
      children,
      overlayClassName,
      className,
      popperOptions,
      closeOnClick = "always",
      closeOnWheel,
      arrow,
      closeOnEsc = true,
      outOfContainer,
      overlayProps,
    }) => {
    const innerId = useMemo(() => id || v1(), [ id ]);
    const overlayId = useId();

    const [ popperElement, setPopperElement ] = useState(null);
    const popperProps = usePopper(referenceElement, popperElement, popperOptions);

    useEffect(() => {
      if (isOpen) {
        const wheelListener = (e: WheelEvent) => {
          if (closeOnWheel) {
            onRequestClose?.();
          }
        };
        const keyDownListener = (e: KeyboardEvent) => {
          if (closeOnEsc && e.key === "Escape") {
            onRequestClose?.();
          }
        };
        const bubblingListener = (e: MouseEvent) => {
          if (closeOnClick === "always") {
            onRequestClose?.();
          } else if (closeOnClick === "outOfDropDown") {
            if (e.target) {
              if (!document.getElementById(innerId)
                ?.contains(e.target as Node)) {
                onRequestClose?.();
              }
            }
          } else if (closeOnClick === "outOfContainer") {
            if (e.target) {
              if (!outOfContainer
                ?.contains(e.target as Node)) {
                onRequestClose?.();
              }
            }
          } else if (closeOnClick === "overlay") {
            if ((e.target as HTMLElement).id === overlayId) {
              onRequestClose?.();
            }
          }
        };
        const capturingListener = () => {
          document.addEventListener("click", bubblingListener);
        };
        document.addEventListener("keydown", keyDownListener);
        document.addEventListener("wheel", wheelListener);
        document.addEventListener("click", capturingListener, { capture: true });
        return () => {
          document.removeEventListener("keydown", keyDownListener);
          document.removeEventListener("wheel", wheelListener);
          document.removeEventListener("click", bubblingListener);
          document.removeEventListener("click", capturingListener, { capture: true });
        };
      }
    }, [ isOpen, onRequestClose, closeOnClick, closeOnWheel, innerId, closeOnEsc, overlayId, outOfContainer ]);

    const portalElement = document.querySelector("#popperPortal") as any;

    return (
      (portalElement && isOpen) ?
        createPortal(
          <div
            {...overlayProps}
            id={overlayId}
            className={classNames(styles.overlay, overlayClassName)}
          >
            <div
              id={innerId}
              className={className}
              ref={setPopperElement as any}
              style={popperProps.styles.popper}
              {...popperProps.attributes.popper}
            >
              {children}
              {arrow && cloneElement(arrow as any, {
                style: popperProps.styles?.arrow,
                ...popperProps.attributes?.arrow,
              })}
            </div>
          </div>,
          portalElement,
        )
        : null
    );
  });

export default Dropdown;
